// @ts-check

import { api } from '@/api'
import { getIndustries } from '@/api/industries'
import { getUser } from '@/api/user'
import { companyStatus, service, swanAccountStatus } from '@/core/constants'
import { FORMAT_VALUE_DATE, stringToDate } from '@/helpers/dates'
import { featureEnabled } from '@/helpers/functions'
import { resetUser, updateCompany } from '@/helpers/marketing'
import { useLanguageStore } from '@/store/language'
import * as Sentry from '@sentry/vue'
import * as _ from 'lodash-es'
import { getField, updateField } from 'vuex-map-fields'
import {
  initPurchaseInvoiceState,
  purchaseInvoiceActions,
  purchaseInvoiceGetters,
  purchaseInvoiceMutations
} from './settings/PurchaseInvoiceSettings'

export const LOCAL_STORAGE_KEY_COMPANYID = 'ukkopro-company-id'

/** @returns {CompanyModuleState} */
export const initCompanyState = () => ({
  company: null,
  // @ts-expect-error - localStorage.getItem returns string | null which doesn't match expected type
  companyId: localStorage.getItem(LOCAL_STORAGE_KEY_COMPANYID),
  bankAccounts: [],
  user: null,
  industries: null,
  founderDetails: null,
  ...initPurchaseInvoiceState()
})

/**
 * @type {CompanyGetterTree}
 */
export const companyGetters = {
  ...purchaseInvoiceGetters,
  getField,
  consentGiven({ company }) {
    return company?.financing_consent_given ?? false
  },
  approvedTermsId({ company }) {
    return company?.approved_terms_id ?? null
  },
  foundingStatus({ company }) {
    return company?.founding_status ?? null
  },
  allowedToAccessOyFoundingProcess: (_state, getters) => {
    return (
      !getters.hasBusinessId &&
      getters.isFoundingNewBusiness &&
      getters.foundingStatus !== 'ltd_step_company_completed'
    )
  },
  onboardingCompleted: ({ company }) => {
    return !!company?.wizard_completed && !!company?.is_signed
  },
  isFoundingNewBusiness: ({ company }) => {
    return company?.is_company_founding ?? false
  },
  wizardCompleted: (state) => {
    return state.company?.wizard_completed ?? null
  },
  isInDemoMode: ({ company }) => !!company?.is_in_demo_mode,
  isOldCustomer: ({ company }) => !!company?.is_old_customer,
  isCourier: ({ company }) =>
    company?.courier_info?.is_partner_courier ?? false,
  courierPartners: ({ company }) =>
    company?.courier_info?.courier_partners ?? [],
  isKycRequired: ({ company }) => !!company?.is_kyc_required,
  isFinancingAvailable: ({ company }) =>
    !!company?.can_apply_for_invoice_financing,
  financingConsentGiven: ({ company }) => !!company?.financing_consent_given,
  hasSentFirstInvoice: (state) => {
    return state.company?.first_invoice_sent ?? false
  },
  hasBankAccount: (state) => {
    if (process.env.VUE_APP_SERVICE === service.TLP) {
      return !!state.user?.account_number
    }

    return !!state.company?.bank_accounts?.length
  },
  hasLinkedBankAccount: (state) => {
    return !!state.company?.has_bank_account_details
  },
  hasBusinessId: ({ company }) => {
    return !!company?.business_id
  },
  company: (state) => {
    return state.company
  },
  founderDetails: (state) => {
    return state.founderDetails
  },
  isAdmin: ({ user }) => {
    return user?.role === '0'
  },
  isVatLiable: ({ company }) => {
    return !!company?.is_vat_responsible
  },
  canSendInvoice: ({ company }) => {
    return !!company?.can_send_invoice
  },
  hasSignedPoa: ({ company }) => {
    return !!company?.is_signed
  },
  isSuomifiAuthorized: ({ company }) => {
    return !!company?.is_suomifi_authorized
  },
  isSuomifiAuthorizationRequestProcessed: (state) => {
    return !!state.company?.suomifi_authorization_request_processed_date
  },
  companyId: (state) => state.companyId,
  user: ({ user }) => user,
  address: ({ company }) => company?.address ?? null,
  isWsConnected: ({ company }) => !!company?.is_ws_connected,
  isSwanConnected: (state) => {
    return state.company?.swan_status === 'connected'
  },
  isSwanPending: (state) => {
    return state.company?.swan_status === 'pending'
  },
  isSwanClosing: (state) => {
    return [swanAccountStatus.CLOSING].includes(state.company?.swan_status)
  },
  isSwanClosingOrClosed: (state) => {
    return [swanAccountStatus.CLOSED, swanAccountStatus.CLOSING].includes(
      state.company?.swan_status
    )
  },
  isSwanVerificationNotStarted: (state) => {
    return state.company?.swan_verification_status === 'not_started'
  },
  isSwanConnectedOrPending: (state) => {
    return [swanAccountStatus.CONNECTED, swanAccountStatus.PENDING].includes(
      state.company?.swan_status
    )
  },
  canApplyForBusinessAccount: (state) => {
    return !!state.company?.can_apply_for_business_account
  },
  canMarkExpenseAsPaid: (state) => {
    return state.company?.can_mark_expense_as_paid ?? true
  },
  hasCompanyData: (state) => {
    if (state.user?.role === '0') {
      return true
    }
    return !!state.company && !!state.companyId
  },
  hasCompletedRegistrationSteps: (state) => {
    return state.company?.has_completed_registration_steps ?? false
  },
  companyIsActive: (state) => {
    if (!state.company) {
      return true
    }

    return state.company?.status?.status === companyStatus.ACTIVE
  },
  reasonsForInactivity: (state) => {
    return state.company?.status.reason ?? []
  },
  isScreeningRestricted: (state) => {
    const restrictions = [
      'kyc_questionnaire_answer_required',
      'customer_screening_hit'
    ]
    return restrictions.some((restriction) =>
      state.company?.status.reason.includes(restriction)
    )
  },
  accountLockedForLateKyc: ({ company }) => {
    return !!company?.status.reason.includes(
      'kyc_questionnaire_answer_required'
    )
  },
  answerKycByDate: ({ company }) => {
    return company?.answer_kyc_by_date ?? null
  },
  features: ({ company }) => {
    return company?.feature ?? []
  },
  hasFeatureEnabled:
    (_state, { features }) =>
    (featureName) =>
      featureEnabled(features, featureName),
  allowEnableBankingConnection: (state, getters) => {
    return getters.hasFeatureEnabled('psd2_connection')
  },
  billingPeriodEndDate: (state) => {
    if (!state.company || !state.company.billing_period_end_date) {
      return null
    }
    return stringToDate(
      state.company.billing_period_end_date,
      FORMAT_VALUE_DATE
    )
  },
  industry: ({ industries, company }) => {
    if (!industries) {
      return null
    }

    return industries.find(({ id: industryId }) => {
      return industryId === company?.company_industry_id
    })
  },
  isTaxReturnQuestionnairePeriod: ({ company }) => {
    return company?.is_tax_return_questionnaire_period ?? false
  },
  canCreateEntries: (state) => {
    return !!state.company?.can_create_entries
  }
}

/**
 * @type {CompanyMutationTree}
 */
export const companyMutations = {
  updateField,
  ...purchaseInvoiceMutations,
  updateCompanyData: (state, companyData) => {
    state.company = { ...state.company, ...companyData }
  },
  setUserData: (state, user) => {
    state.user = user
  },
  /**
   * Updates the state with the given company data.
   */
  setCompanyData: (state, company) => {
    state.company = { ...company }
    state.companyId = company.id
    localStorage.setItem(LOCAL_STORAGE_KEY_COMPANYID, `${company.id}`)

    state.company.address = company.address ?? {
      address: '',
      address_2: '',
      zip: '',
      post_office: '',
      country: ''
    }

    updateCompany({ company, user: state.user })
  },
  setBankAccounts: (state, accounts) => {
    state.bankAccounts = accounts
  },
  clearAllData: (state) => {
    state.company = null
    state.companyId = null
    state.bankAccounts = []
    state.user = null

    localStorage.removeItem(LOCAL_STORAGE_KEY_COMPANYID)

    // Update GTM
    resetUser()
  },
  setIndustries: (state, industries) => {
    state.industries = industries
  },
  setFounderDetails: (state, founderDetails) => {
    state.founderDetails = founderDetails
  }
}

/**
 * @type {CompanyActionTree}
 */
export const companyActions = {
  ...purchaseInvoiceActions,
  async init({ dispatch, getters }) {
    if (getters.hasCompanyData) {
      return
    }

    await dispatch('getUserCompany')
  },
  getUserCompany: async ({ commit }) => {
    const userData = await getUser()

    Sentry.getCurrentScope().setUser({ id: userData.id })

    const companyUserData = userData.user_companies
      ? JSON.parse(JSON.stringify(userData.user_companies[0]))
      : {}

    const user = Object.assign(
      JSON.parse(JSON.stringify(userData)),
      companyUserData
    )

    delete user.company
    delete user.user_companies

    // This is not the ideal place for this but user details are not fetched anywhere
    // else on login.
    // Also if statement needed here because otherwise on registration language is forced to FI
    if (user.language) {
      const { changeLanguage } = useLanguageStore()
      await changeLanguage(user.language)
    }

    commit('setUserData', user)

    if (userData.user_companies) {
      const { company } = userData.user_companies[0]
      Sentry.getCurrentScope()
        .setUser({ id: userData.id })
        .setContext('company', { company_id: company.id })

      if (!company.address) {
        company.address = {
          address: '',
          address_2: '',
          zip: '',
          post_office: '',
          country: ''
        }
      }
      commit('setCompanyData', company)

      if (_.has(company, 'bank_accounts')) {
        commit('setBankAccounts', company.bank_accounts)
      }
    }
  },
  setUserCompanyDetails({ commit }, userData) {
    Sentry.getCurrentScope().setUser({ id: userData.id })

    const companyUserData = userData.user_companies
      ? JSON.parse(JSON.stringify(userData.user_companies[0]))
      : {}

    const user = Object.assign(
      JSON.parse(JSON.stringify(userData)),
      companyUserData
    )

    delete user.company
    delete user.user_companies

    commit('setUserData', user)

    if (userData.user_companies) {
      const { company } = userData.user_companies[0]

      Sentry.getCurrentScope()
        .setUser({ id: userData.id })
        .setContext('company', { company_id: company.id })

      if (!company.address) {
        company.address = {
          address: '',
          address_2: '',
          zip: '',
          post_office: '',
          country: ''
        }
      }
      commit('setCompanyData', company)

      if (_.has(company, 'bank_accounts')) {
        commit('setBankAccounts', company.bank_accounts)
      }
    }
  },
  saveCompany: async ({ state, commit }, payload) => {
    const { data: company } = await api.patch(
      `company/${state.companyId}`,
      payload
    )
    commit('setCompanyData', company)
  },
  fetchIndustries: async ({ commit }) => {
    const industries = await getIndustries({})
    commit('setIndustries', industries)
    return industries
  },
  fetchFounderDetails: async ({ commit }) => {
    const { data: founderDetails } = await api.get('company-founding/founder')
    commit('setFounderDetails', founderDetails)
    return founderDetails
  }
}

export const companyModule = {
  namespaced: true,
  state: initCompanyState,
  getters: companyGetters,
  mutations: companyMutations,
  actions: companyActions
}

export default companyModule
